/**
 * ============================================================================
 * ThresholdMatrixAdmin — Phase 6b frontend (Threshold Matrix Admin)
 * Owner: Claude 3 (Frontend & UX).
 * Route: /quality-metrics/thresholds
 * Purpose: per-metric editor for the green/amber/red traffic-light bands that
 *          back the Quality Metrics Dashboard. Each row is one metric with
 *          coloured boundary chips and an Edit button. The edit dialog routes
 *          through the GMP-Critical dual-sign pattern.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading state shows skeleton rows.
 *   - Error state shows a refined envelope with a Retry button.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ThresholdMatrixAdmin.module.css';
import { useThresholdsQuery, type MetricThreshold } from './useThresholdsQuery';
import { ThresholdBandsDialog } from './ThresholdBandsDialog';

type DialogMode = { kind: 'closed' } | { kind: 'edit'; threshold: MetricThreshold };

function formatBoundary(value: number | undefined, kind: MetricThreshold['kind']): string {
  if (value == null) return '—';
  const suffix = kind === 'PERCENTAGE' ? '%' : kind === 'DAYS' ? 'd' : '';
  const rounded =
    kind === 'PERCENTAGE'
      ? value.toFixed(1).replace(/\.0$/, '')
      : String(value);
  return `${rounded}${suffix}`;
}

export function ThresholdMatrixAdmin() {
  const { t, i18n } = useTranslation();
  const thresholdsQ = useThresholdsQuery();
  const [dialog, setDialog] = useState<DialogMode>({ kind: 'closed' });

  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
      }),
    [i18n.language],
  );

  const items: MetricThreshold[] = thresholdsQ.data?.items ?? [];
  const isEmpty =
    !thresholdsQ.isLoading && !thresholdsQ.isError && items.length === 0;

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('qualityMetrics.thresholds.eyebrow', {
          defaultValue: 'Quality',
        })}
        title={t('qualityMetrics.thresholds.title', {
          defaultValue: 'Quality Threshold Matrix',
        })}
        subtitle={t('qualityMetrics.thresholds.subtitle', {
          defaultValue:
            'Per-metric green / amber / red bands driving the Quality Metrics Dashboard.',
        })}
      />

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('qualityMetrics.thresholds.list.title', { defaultValue: 'Metrics' })}{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              ({nf.format(items.length)})
            </span>
          </h2>
        </CardHeader>
        <CardBody>
          {thresholdsQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          )}

          {thresholdsQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('qualityMetrics.thresholds.errors.loadFailed', {
                  defaultValue: 'Could not load quality threshold matrix.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => thresholdsQ.refetch()}
              >
                {t('qualityMetrics.actions.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {isEmpty && (
            <div className={styles.emptyState}>
              <h3 className={styles.emptyTitle}>
                {t('qualityMetrics.thresholds.empty.title', {
                  defaultValue: 'No quality thresholds configured yet.',
                })}
              </h3>
              <p>
                {t('qualityMetrics.thresholds.empty.body', {
                  defaultValue:
                    'Thresholds are seeded with metrics-svc deploys. Contact your QA Director if this list is empty.',
                })}
              </p>
            </div>
          )}

          {!thresholdsQ.isLoading &&
            !thresholdsQ.isError &&
            items.length > 0 && (
              <table
                className={wstyles.table}
                aria-label={t('qualityMetrics.thresholds.tableAria', {
                  defaultValue: 'Quality metric thresholds',
                })}
              >
                <thead>
                  <tr>
                    <th scope="col">
                      {t('qualityMetrics.thresholds.columns.metric', {
                        defaultValue: 'Metric',
                      })}
                    </th>
                    <th scope="col">
                      {t('qualityMetrics.thresholds.columns.polarity', {
                        defaultValue: 'Polarity',
                      })}
                    </th>
                    <th scope="col">
                      {t('qualityMetrics.thresholds.columns.bands', {
                        defaultValue: 'Bands',
                      })}
                    </th>
                    <th scope="col">
                      {t('qualityMetrics.thresholds.columns.updatedAt', {
                        defaultValue: 'Last updated',
                      })}
                    </th>
                    <th scope="col">
                      {t('qualityMetrics.thresholds.columns.actions', {
                        defaultValue: 'Actions',
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const isLowerBetter = row.polarity === 'LOWER_IS_BETTER';
                    const metricLabel = t(
                      `qualityMetrics.metric.${row.metric_key}.label`,
                      { defaultValue: row.label },
                    );
                    return (
                      <tr key={row.metric_key}>
                        <td>
                          <strong>{metricLabel}</strong>
                          <div className={styles.kindHint}>
                            <code>{row.metric_key}</code> ·{' '}
                            {t(`qualityMetrics.thresholds.kind.${row.kind}`, {
                              defaultValue: row.kind,
                            })}
                          </div>
                        </td>
                        <td>
                          {t(
                            `qualityMetrics.thresholds.polarity.${row.polarity}.title`,
                            {
                              defaultValue: isLowerBetter
                                ? 'Lower is better'
                                : 'Higher is better',
                            },
                          )}
                        </td>
                        <td>
                          <div className={styles.bandChips}>
                            {isLowerBetter ? (
                              <>
                                <BandChip
                                  color="green"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.greenMax',
                                    { defaultValue: '≤ {{v}}', v: formatBoundary(row.boundaries.green_max, row.kind) },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.greenMaxAria',
                                    {
                                      defaultValue: 'Green band up to {{v}}',
                                      v: formatBoundary(row.boundaries.green_max, row.kind),
                                    },
                                  )}
                                />
                                <BandChip
                                  color="amber"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.amberMax',
                                    { defaultValue: '≤ {{v}}', v: formatBoundary(row.boundaries.amber_max, row.kind) },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.amberMaxAria',
                                    {
                                      defaultValue: 'Amber band up to {{v}}',
                                      v: formatBoundary(row.boundaries.amber_max, row.kind),
                                    },
                                  )}
                                />
                                <BandChip
                                  color="red"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.redAbove',
                                    {
                                      defaultValue: '> {{v}}',
                                      v: formatBoundary(row.boundaries.amber_max, row.kind),
                                    },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.redAboveAria',
                                    {
                                      defaultValue: 'Red band above {{v}}',
                                      v: formatBoundary(row.boundaries.amber_max, row.kind),
                                    },
                                  )}
                                />
                              </>
                            ) : (
                              <>
                                <BandChip
                                  color="green"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.greenMin',
                                    { defaultValue: '≥ {{v}}', v: formatBoundary(row.boundaries.green_min, row.kind) },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.greenMinAria',
                                    {
                                      defaultValue: 'Green band at or above {{v}}',
                                      v: formatBoundary(row.boundaries.green_min, row.kind),
                                    },
                                  )}
                                />
                                <BandChip
                                  color="amber"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.amberMin',
                                    { defaultValue: '≥ {{v}}', v: formatBoundary(row.boundaries.amber_min, row.kind) },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.amberMinAria',
                                    {
                                      defaultValue: 'Amber band at or above {{v}}',
                                      v: formatBoundary(row.boundaries.amber_min, row.kind),
                                    },
                                  )}
                                />
                                <BandChip
                                  color="red"
                                  label={t(
                                    'qualityMetrics.thresholds.chip.redBelow',
                                    {
                                      defaultValue: '< {{v}}',
                                      v: formatBoundary(row.boundaries.amber_min, row.kind),
                                    },
                                  )}
                                  aria={t(
                                    'qualityMetrics.thresholds.chip.redBelowAria',
                                    {
                                      defaultValue: 'Red band below {{v}}',
                                      v: formatBoundary(row.boundaries.amber_min, row.kind),
                                    },
                                  )}
                                />
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {row.updated_at_utc ? (
                            <time dateTime={row.updated_at_utc}>
                              {dateFmt.format(new Date(row.updated_at_utc))}
                            </time>
                          ) : (
                            <span className={styles.placeholderDash}>—</span>
                          )}
                          <div className={styles.versionHint}>
                            v{row.version}
                          </div>
                        </td>
                        <td>
                          <div className={wstyles.rowActions}>
                            <Button
                              size="sm"
                              variant="tertiary"
                              onClick={() =>
                                setDialog({ kind: 'edit', threshold: row })
                              }
                              aria-label={t(
                                'qualityMetrics.thresholds.actions.editAria',
                                {
                                  defaultValue:
                                    'Edit thresholds for {{metric}} — requires dual signature',
                                  metric: metricLabel,
                                },
                              )}
                            >
                              {t('qualityMetrics.thresholds.actions.edit', {
                                defaultValue: 'Edit',
                              })}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </CardBody>
      </Card>

      {dialog.kind === 'edit' && (
        <ThresholdBandsDialog
          threshold={dialog.threshold}
          onClose={() => setDialog({ kind: 'closed' })}
          onSaved={() => {
            setDialog({ kind: 'closed' });
            thresholdsQ.refetch();
          }}
        />
      )}
    </div>
  );
}

interface BandChipProps {
  color: 'green' | 'amber' | 'red';
  label: string;
  aria: string;
}

function BandChip({ color, label, aria }: BandChipProps) {
  const classes = {
    green: styles.chipGreen,
    amber: styles.chipAmber,
    red: styles.chipRed,
  }[color];
  return (
    <span className={`${styles.bandChip} ${classes}`} aria-label={aria}>
      {label}
    </span>
  );
}
